import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { signOut, useSession } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { askStaffSignInCode, needsStaffCode, tokenClaims } from '@/lib/staffSignInCode';

interface ProtectedRouteProps {
  children: ReactNode;
}

// Sessions that already went through the staff sign-in code on this page
// load (passed, or skipped in Warn mode) — so navigating between protected
// routes never asks twice.
const codeDone = new Set<string>();

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-brix-surface">
      <Loader2 size={20} className="animate-spin text-brix-primary" />
    </div>
  );
}

/**
 * Wraps an authenticated route. Renders a brand-aligned spinner while we
 * resolve the initial Supabase session, then either renders the children or
 * redirects to /login, preserving the original path so post-login we can
 * send the user back where they came from.
 *
 * A STAFF session that hasn't passed the hub's texted sign-in code gets the
 * gateway's code card first (lib/staffSignInCode.ts). Customer sessions are
 * never asked — their token carries no staff role.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { session, loading } = useSession();
  const location = useLocation();
  const token = session?.access_token ?? null;
  const sid = token ? String(tokenClaims(token).session_id || token) : '';
  const asking = !!token && needsStaffCode(token) && !codeDone.has(sid);
  const [, rerender] = useState(0);

  useEffect(() => {
    if (!asking) return;
    let alive = true;
    void askStaffSignInCode({
      token: async () => (await supabase.auth.getSession()).data.session?.access_token ?? null,
      // Same session, new token — stamped mfa:true by the access-token hook.
      refresh: () => supabase.auth.refreshSession(),
      signOut: async () => {
        await signOut();
      },
    }).then(() => {
      codeDone.add(sid);
      if (alive) rerender((n) => n + 1);
    });
    return () => {
      alive = false;
    };
  }, [asking, sid]);

  if (loading) return <Spinner />;

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (asking) return <Spinner />;

  return <>{children}</>;
}
